/**
 * Currency Formatter Utility
 * Formats numbers as currency strings with proper localization
 * Supports all 217 ISO 4217 currencies with dynamic locale detection
 */

/**
 * Get user's preferred locale for formatting
 * Priority: localStorage userLocale → browser language → en-US fallback
 *
 * @returns Locale string (e.g., 'en-US', 'de-DE', 'ar-SA')
 */
const getUserLocale = (): string => {
  const storedLocale = localStorage.getItem('userLocale');
  return storedLocale || navigator.language || 'en-US';
};

/**
 * Format a number as currency with locale-aware formatting
 * Uses organization currency from localStorage
 * Falls back to USD if no currency is set
 *
 * Supports all 217 ISO 4217 currencies (USD, EUR, LKR, SAR, AED, BHD, etc.)
 * Locale-aware formatting: decimal separators, symbol positioning, digit grouping
 *
 * @param amount - The amount to format
 * @param currency - Optional currency code (e.g., 'USD', 'LKR', 'EUR')
 * @returns Formatted currency string
 *
 * @example
 * // US user with EUR: "€1,234.56"
 * formatCurrency(1234.56, 'EUR')
 *
 * // German user with EUR: "1.234,56 €"
 * formatCurrency(1234.56, 'EUR')
 *
 * // Saudi user with SAR: "١٬٢٣٤٫٥٦ ر.س." (Arabic format)
 * formatCurrency(1234.56, 'SAR')
 */
export const formatCurrency = (amount: number, currency?: string): string => {
  const storedCurrency = localStorage.getItem('currency');
  const orgCurrency = currency || storedCurrency || 'USD';
  const userLocale = getUserLocale();

  try {
    // Primary: Format with user's locale
    const formatter = new Intl.NumberFormat(userLocale, {
      style: 'currency',
      currency: orgCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(amount);
  } catch (error) {
    // Fallback 1: Try with safe locale (en-US)
    try {
      const fallbackFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: orgCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return fallbackFormatter.format(amount);
    } catch (secondError) {
      // Fallback 2: Generic format without symbol lookup
      console.warn('Currency formatting failed for:', orgCurrency, secondError);
      return `${orgCurrency} ${amount.toFixed(2)}`;
    }
  }
};

/**
 * Get currency symbol for a given currency code
 * Extracts symbol from formatted currency value
 *
 * @param currency - Optional currency code (e.g., 'USD', 'EUR', 'LKR')
 * @returns Currency symbol (e.g., '$', '€', 'Rs')
 *
 * @example
 * getCurrencySymbol('USD') // "$"
 * getCurrencySymbol('EUR') // "€"
 * getCurrencySymbol('LKR') // "Rs"
 */
export const getCurrencySymbol = (currency?: string): string => {
  const storedCurrency = localStorage.getItem('currency');
  const orgCurrency = currency || storedCurrency || 'USD';

  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: orgCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);

    // Extract symbol by removing digits, spaces, and punctuation
    return formatted.replace(/[\d\s.,]/g, '').trim();
  } catch (error) {
    // Return currency code as fallback
    return orgCurrency;
  }
};

/**
 * Format a number as currency without symbol
 * Uses locale-aware formatting for decimal separators and digit grouping
 * Useful for input fields or when you want to show only the number
 *
 * @param amount - The amount to format
 * @returns Formatted number string (e.g., "1,234.56" or "1.234,56")
 */
export const formatCurrencyNumber = (amount: number): string => {
  const userLocale = getUserLocale();

  try {
    const formatter = new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(amount);
  } catch (error) {
    console.warn('Number formatting failed, using fallback:', error);
    return amount.toFixed(2);
  }
};
